import api from '@/factories/api';
import { compareStrings } from '@/filters/helpers';

export default {
  namespaced: true,

  state: {
    formSubmissions: [],
    formSubmissionsLoading: false,
    formSubmissionsLoaded: false,
    search: '',
    schemas: {},
    schemasLoading: {},
  },

  getters: {
    formSubmissions: (state) => state.formSubmissions,
    formSubmissionsLoading: (state) => state.formSubmissionsLoading,
    formSubmissionsLoaded: (state) => state.formSubmissionsLoaded,
    submissionSchema: (state) => (collectionId) => {
      const schemaData = state.schemas[collectionId];
      return schemaData?.schema || {};
    },
    submissionSections: (state) => (collectionId) => {
      const schemaData = state.schemas[collectionId];
      return schemaData?.sections || [];
    },
    submissionSchemaFull: (state) => (collectionId) =>
      state.schemas[collectionId] || null,
    sortedSubmission: (state) => {
      const statusOrder = {
        draft: 1,
        pending: 2,
        completed: 3,
      };

      // Filter by search term against form name
      let filtered = state.formSubmissions;
      if (state.search && state.search.trim()) {
        filtered = filtered.filter((submission) => {
          const formName = submission?.form?.name || '';
          return compareStrings(formName, state.search.trim());
        });
      }

      // Sort by status
      return [...filtered].sort((a, b) => {
        const orderA = statusOrder[a.status] || 999;
        const orderB = statusOrder[b.status] || 999;
        return orderA - orderB;
      });
    },
    singleSubmission: (state) => (submissionId) => {
      return state.formSubmissions.find((s) => s.id === submissionId) || null;
    },
    submissionProgress: (state) => (submissionId) => {
      return 0;
    },
  },

  mutations: {
    setFormSubmissions(state, submissions) {
      state.formSubmissions = submissions;
    },

    setFormSubmissionsLoading(state, loading) {
      state.formSubmissionsLoading = loading;
    },

    updateFormData(state, { formId, fieldValues }) {
      const submission = state.formSubmissions.find((f) => f.id === formId);
      if (submission) {
        // Update submission with filled field values
        Object.assign(submission, fieldValues);
      }
    },

    setSchema(state, { collectionId, schema }) {
      state.schemas = {
        ...state.schemas,
        [collectionId]: schema,
      };
    },

    setSchemaLoading(state, { collectionId, loading }) {
      state.schemasLoading = {
        ...state.schemasLoading,
        [collectionId]: loading,
      };
    },

    setSearch(state, search) {
      state.search = search;
    },

    addOrUpdateSubmission(state, submission) {
      const index = state.formSubmissions.findIndex(
        (s) => s.id === submission.id
      );
      if (index !== -1) {
        // Update existing submission
        state.formSubmissions.splice(index, 1, submission);
      } else {
        // Add new submission
        state.formSubmissions.push(submission);
      }
    },

    setFormSubmissionsLoaded(state, loaded) {
      state.formSubmissionsLoaded = loaded;
    },
  },

  actions: {
    async loadFormSubmissions({ commit }) {
      try {
        const forms = await api().get('portal/form/submissions');
        commit('setFormSubmissions', forms);
        return forms;
      } finally {
        commit('setFormSubmissionsLoaded', true);
      }
    },

    async loadSingleSubmission({ commit }, submissionId) {
      commit('setFormSubmissionsLoading', true);

      try {
        const submission = await api().get(
          `portal/form/submissions/${submissionId}`
        );
        commit('addOrUpdateSubmission', submission);
      } finally {
        commit('setFormSubmissionsLoading', false);
      }
    },

    async loadSubmissionSchema({ commit, state }, collectionId) {
      // Don't reload if already loaded or loading
      if (state.schemas[collectionId] || state.schemasLoading[collectionId]) {
        return state.schemas[collectionId];
      }

      commit('setSchemaLoading', { collectionId, loading: true });

      try {
        const schema = await api().get(
          `portal/form/submission_collections/${collectionId}`
        );
        commit('setSchema', { collectionId, schema });
        return schema;
      } finally {
        commit('setSchemaLoading', { collectionId, loading: false });
      }
    },

    async updateSubmission({ commit }, { submissionId, data }) {
      commit('setFormSubmissionsLoading', true);

      try {
        const updatedSubmission = await api().put(
          `portal/form/submissions/${submissionId}`,
          data
        );

        commit('addOrUpdateSubmission', updatedSubmission);
        return updatedSubmission;
      } finally {
        commit('setFormSubmissionsLoading', false);
      }
    },
  },
};
